import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  OAuthProvider,
  getCurrentUser,
  signInWithEmail,
  signOut as signOutRequest,
  startOAuthSignIn,
  type AppwriteUser,
} from './api';
import { toAuthError, type AuthError } from './errors';

/**
 * The web counterpart of `AuthenticationBloc`.
 *
 * The three states map one-for-one:
 *   unknown         → we don't know yet whether a session exists (splash)
 *   unauthenticated → sign-in screen
 *   authenticated   → the application shell
 *
 * Email and OAuth sign-in both funnel through `features/auth/api.ts`; nothing
 * else in the app creates Appwrite sessions.
 */
export type AuthStatus = 'unknown' | 'unauthenticated' | 'authenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AppwriteUser | null;
  userId: string | null;
  /** True while a sign-in attempt is in flight. */
  busy: boolean;
  signingOut: boolean;
  error: AuthError | null;
  clearError: () => void;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithProvider: (provider: OAuthProvider) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-reads the session from Appwrite, e.g. after an OAuth redirect. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('unknown');
  const [user, setUser] = useState<AppwriteUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);

  // Guards against a state update after unmount, and against two concurrent
  // sign-in attempts from a double-submitted form.
  const mounted = useRef(true);

  // A ref rather than state, so the re-entrancy guard is read synchronously
  // within the same tick a double-submitted form would arrive in. This is the
  // `if (state.showLoading) return;` guard from the Flutter bloc.
  const busyRef = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const current = await getCurrentUser();
      if (!mounted.current) return;
      setUser(current);
      setStatus(current === null ? 'unauthenticated' : 'authenticated');
    } catch (caught) {
      // A transient failure at startup must not lock the user out: fall back
      // to the sign-in screen with the reason shown.
      console.warn('Session check failed:', caught);
      if (!mounted.current) return;
      setUser(null);
      setStatus('unauthenticated');
      setError(toAuthError(caught));
    }
  }, []);

  // Dispatched once at startup, the equivalent of the bloc's `AppStarted`.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);

    try {
      const signedIn = await signInWithEmail(email, password);
      if (!mounted.current) return;
      setUser(signedIn);
      setStatus('authenticated');
    } catch (caught) {
      console.warn('Sign-in failed:', caught);
      if (!mounted.current) return;
      setUser(null);
      setStatus('unauthenticated');
      setError(toAuthError(caught));
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [busyRef]);

  const signInWithProvider = useCallback(async (provider: OAuthProvider) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);

    const origin = window.location.origin;
    try {
      // This navigates away on success; nothing after it runs in that case.
      await startOAuthSignIn(provider, `${origin}/auth/callback`, `${origin}/sign-in?oauth=failed`);
    } catch (caught) {
      console.warn('OAuth sign-in failed:', caught);
      if (!mounted.current) return;
      setError(toAuthError(caught));
      busyRef.current = false;
      setBusy(false);
    }
  }, [busyRef]);

  const signOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await signOutRequest();
    } finally {
      if (mounted.current) {
        setUser(null);
        setStatus('unauthenticated');
        setError(null);
        setSigningOut(false);
      }
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      userId: user?.$id ?? null,
      busy,
      signingOut,
      error,
      clearError,
      signInWithPassword,
      signInWithProvider,
      signOut,
      refresh,
    }),
    [
      status,
      user,
      busy,
      signingOut,
      error,
      clearError,
      signInWithPassword,
      signInWithProvider,
      signOut,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used inside an <AuthProvider>.');
  }
  return context;
}
